import { menuEditorActions, menuEditorSelectors } from "features/editor/editorSlice";
import { EditorItem, useUpdateProperty } from "features/editor/editorUtils";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { assoc, equals } from "ramda";
import { ReactNode, useEffect } from "react";
import { LabelDocument, LabelItem } from "./LabelDocumentModel";
import { labelEditorActions, labelEditorSelectors } from "./labelEditorSlice";
import { applyVariation, resetBackground, resetNode, resetTemplate, updateNode } from "./labelEditorUtils";
import { LabelDocumentRenderer } from "./LabelRenderer";


export interface LabelEditorProps {
    labelItem: EditorItem<LabelItem>
}

export function LabelEditor(props: LabelEditorProps) {
    const document = useAppSelector(menuEditorSelectors.document) as LabelDocument;

    const focusedItem = useAppSelector(menuEditorSelectors.focusedItem);
    const dispatch = useAppDispatch();
    const updateProperty = useUpdateProperty(props.labelItem);

    useEffect(()=>{
        dispatch(labelEditorActions.init());

        return () => {
            dispatch(labelEditorActions.init());
        }
    }, [props.labelItem.itemKey, focusedItem?.itemKey, dispatch]);

    const labelEditorItemKey = useAppSelector(labelEditorSelectors.itemKey);
    const activeNodeId = useAppSelector(labelEditorSelectors.activeNodeId);

    if (!equals(labelEditorItemKey, props.labelItem.itemKey)) {
        // initialization in process
        return <div />
    }

    const labelItem = props.labelItem.menuItem;
    const templateData = labelItem.templateData;
    const renderer = new LabelDocumentRenderer(document, labelItem);

    let editor: ReactNode|null = null;
    
    if (activeNodeId != null) {
        editor = renderer.renderEditor(
            activeNodeId,
            {
                onNodeClick: nodeId => {
                    dispatch(labelEditorActions.activateNodeById(nodeId))
                }
            },
            {
                onNodeUpdated: node => {
                    updateProperty(
                        "templateData",
                        updateNode(node, templateData)
                    );
                },
                onNodeReset: nodeId => {
                    updateProperty(
                        "templateData",
                        resetNode(nodeId, labelItem, document)
                    )
                },
                onVariationApplied: variation => {
                    const updatedTemplate = applyVariation(
                        templateData,
                        labelItem.variation,
                        variation
                    );
                    let mi = assoc("templateData", updatedTemplate, props.labelItem.menuItem);
                    mi = assoc("variation", variation, mi);
                    dispatch(menuEditorActions.editItem({
                        key: props.labelItem.itemKey,
                        newItem: mi
                    }));
                },
                onTemplateReset: () => {
                    updateProperty(
                        "templateData",
                        resetTemplate(labelItem, document)
                    )
                },
                onBackgroundReset: () => {
                    updateProperty(
                        "templateData",
                        resetBackground(labelItem, document)
                    )
                }
            }
        )
    }

    return <div>
        {editor}
    </div>
}

